import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import * as SecureStore from 'expo-secure-store';
import Toast from 'react-native-toast-message';
import { logger, perf } from '../utils/logger';
import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  LoginResponse,
  RegisterResponse,
  VerifyEmailRequest,
  VerificationResponse,
  PasswordResetRequest,
  PasswordResetRequestResponse,
  ResetPasswordRequest,
  UpdateProfileRequest,
  UpdatePasswordRequest,
  UpdateEmailRequest,
  EmailUpdateResponse,
  VerificationTokenResponse,
  UserStats,
  User,
  UploadedFile,
  InventoryItem,
  PaginatedResponse,
  FilesResponse,
  InventoryResponse,
  HealthResponse,
  FeedbackSubmissionRequest,
  FeedbackSubmissionResponse,
  FeedbackTypesResponse,
  UserStatisticsResponse,
  FilesWithInventoryResponse,
  FileWithInventoryResponse,
  PaginationInfo,
  RefreshUrlsResponse,
  FileDetailsResponse,
  InventoryItemDetailsResponse,
  DeleteFileResponse,
  DeleteInventoryItemResponse,
  VectorDeletionResults,
  UpdateInventoryItemRequest,
  UpdateInventoryItemResponse,
  LocationHistoryResponse,
  TopLocationsResponse,
  EnhancedInventoryRequest,
  EnhancedInventoryResponse,
  // Workspaces / Groups (Chunk 8)
  ListGroupsResponse,
  GroupDetail,
  GroupMember,
  CreateGroupRequest,
  PatchGroupRequest,
  InviteMemberRequest,
  InviteMemberResponse,
  AcceptInviteRequest,
  AcceptInviteResponse,
  ReceivedInvitesResponse,
  PatchMemberRoleRequest,
  // Sprint 2 / Chunk 9 — item↔group binding + rules
  AddItemsToGroupRequest,
  AddItemsToGroupResponse,
  GroupInventoryResponse,
  CreateGroupRuleRequest,
  GroupRule,
  // Sprint 3 / Chunk 10A — Custody flow
  HandoffRequest,
  HandoffRequestRequest,
  HandoffRequestResponse,
  ReturnRequest,
  CustodyHistoryResponse,
  CustodyFlipResponse,
  // Sprint 4 / Chunk 10B — Activity feed + recall + cross-group search
  GroupActivityResponse,
  RecallRequest,
  RecallResponse,
  ResolveResponse,
  // Sprint 5 / Chunk 11 — Packs (event-scoped item subsets)
  PackSummary,
  PackDetail,
  PackListResponse,
  CreatePackRequest,
  PatchPackRequest,
  AddPackItemsRequest,
  AddPackItemsResponse,
  WrapPackResponse,
  // Sprint 5 / Chunk 11.5 — Quick-add + voice metadata extract
  QuickAddRequest,
  QuickAddResponse,
  ExtractMetadataRequest,
  ExtractMetadataResponse,
  // Sprint 8 — Notifications (backend#151)
  NotificationListResponse,
  NotificationListParams,
  UnreadCountResponse,
  MarkReadResult,
  MarkAllReadResponse,
  // FE-212 / BE-201 + BE-208 — multi-photo per item
  PhotoSummary,
  PhotoReorderRequest,
  // FE #124 / backend#312 + #313 — scan history + recovery
  ScanHistoryItem,
  ScanHistoryResponse,
  ScanHistoryListParams,
  ScanDetailResponse,
} from './types';
import type {
  ResegmentRequest,
  ResegmentResponse,
  ManualAddInventoryItemRequest,
  ManualAddInventoryItemResponse,
  ManualAnalyzeRequest,
  ManualAnalyzeResponse,
  DeleteInventoryItemV2Request,
  DeleteInventoryItemV2Response,
  ConfirmBatchRequest,
  ConfirmBatchResponse,
  AdminAnnotationFeedbackParams,
  AnnotationFeedbackRow,
} from './scanStreamTypes';
import type {
  CreateShareRequest,
  ShareResponse,
  ShareListResponse,
  ShareManageRequest,
  ShareManageResponse,
  VisibilityUpdateRequest,
  VisibilityUpdateResponse,
  SharingContactsResponse,
  ShareViewResponse,
  ClaimRequest,
  ClaimResponse,
} from './sharingTypes';
import type {
  PublicItemsRequest,
  PublicItemsResponse,
  CreateBorrowRequestSchema,
  BorrowRequestResponse,
  BorrowRequestsQuery,
  BorrowRequestListResponse,
  BorrowRequestActionSchema,
  BorrowRequestActionResponse,
  SentBorrowRequestListResponse,
  SentBorrowCancelResponse,
} from './borrowTypes';
import type {
  SharingContactsRequest,
} from './contactsTypes';

// Use environment variable or fallback to production URL
const API_BASE_URL = process.env.EXPO_PUBLIC_API_BASE_URL || 'https://backend.remembr.xyz';

/**
 * Session-expired callback. The 401 response interceptor clears the
 * stored auth token, but on its own that leaves the Redux auth slice
 * still reporting `isAuthenticated: true`, so AppNavigator doesn't
 * re-route to Login. The app bootstrap (App.tsx) registers a callback
 * here that dispatches `logoutUser()` — setting isAuthenticated to
 * false — so the navigator swaps to the auth stack automatically.
 */
let onSessionExpired: (() => void) | null = null;
export const setOnSessionExpired = (fn: (() => void) | null) => {
  onSessionExpired = fn;
};

console.log('🔗 API_BASE_URL configured as:', API_BASE_URL);
console.log('🔗 Environment variable EXPO_PUBLIC_API_BASE_URL:', process.env.EXPO_PUBLIC_API_BASE_URL);

// FE-251 — module-scoped sequence counter for per-request perf tags so
// concurrent axios calls don't collide on the perf timer's label key.
let _httpReqSeq = 0;

class RemembrApi {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      // Backend's pagination + Zilliz hybrid search take a noisy
      // 5-25 s under realistic load. 30 s left no headroom — list
      // endpoints (esp. /users/inventory) timed out on cold cache.
      // Bumped to 60 s so transient backend slowness doesn't blank
      // the user's screens. Per-endpoint overrides (e.g. confirm
      // batch at 90 s) still apply on top.
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        try {
          // Check if this request should skip authentication
          if ((config as any).skipAuth) {
            console.log('🚫 Skipping Authorization header for public endpoint');
            return config;
          }
          
          let token: string | null = null;
          
          // Try SecureStore first (mobile), fallback to localStorage (web)
          try {
            token = await SecureStore.getItemAsync('auth_token');
          } catch (secureStoreError) {
            if (typeof window !== 'undefined' && window.localStorage) {
              token = window.localStorage.getItem('auth_token');
            }
          }
          
          if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('🔐 REQUEST INTERCEPTOR: Auth token added', {
              url: config.url,
              method: config.method?.toUpperCase(),
              hasToken: !!token,
              tokenPrefix: token ? token.substring(0, 10) + '...' : 'none'
            });
          } else {
            console.log('🚫 REQUEST INTERCEPTOR: No auth token', {
              url: config.url,
              method: config.method?.toUpperCase(),
              hasToken: !!token,
              hasHeaders: !!config.headers
            });
          }
        } catch (error) {
          logger.warn('Failed to get auth token', error);
          console.log('🔐 REQUEST INTERCEPTOR: Error getting token:', error);
        }
        // FE-251 — start a perf timer keyed on a tag we stash on the
        // config object. Each request gets a unique tag so concurrent
        // requests don't clobber each other's measurements. Response
        // + error interceptors read the tag back to compute duration.
        const perfTag = `http:${config.method?.toUpperCase() ?? 'GET'} ${config.url ?? ''}#${++_httpReqSeq}`;
        (config as any).__perfTag = perfTag;
        perf.time(perfTag);
        return config;
      },
      (error) => {
        logger.error('Request interceptor error', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // FE-251 — close the perf timer started in the request
        // interceptor. Use status as extra so a single log line tells
        // you method + url + status + duration.
        const perfTag = (response.config as any).__perfTag as string | undefined;
        if (perfTag) {
          perf.timeEnd(perfTag, { status: response.status });
        }
        console.log('🔗 API Response SUCCESS:', {
          url: response.config.url,
          status: response.status,
          method: response.config.method?.toUpperCase()
        });
        return response;
      },
      async (error) => {
        // FE-251 — close the perf timer even on error so the duration
        // line still emits (helps spot endpoints that fail SLOWLY,
        // not just ones that fail). Tag is on error.config.
        const perfTag = (error.config as any)?.__perfTag as string | undefined;
        if (perfTag) {
          perf.timeEnd(perfTag, {
            status: error.response?.status ?? null,
            errorCode: error.code,
            cancelled: axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError',
          });
        }
        // Phase 3A — Skip ALL error logging when the request was cancelled
        // client-side (component unmount, navigation, debounce abort) or
        // never reached the server (no response object). These are not
        // real failures — Sentry was treating them as API errors and
        // flooding the inbox.
        const isCancelled =
          axios.isCancel(error)
          || error.code === 'ERR_CANCELED'
          || error.name === 'CanceledError'
          || error.name === 'AbortError'
          || (error.response === undefined && error.code !== 'ECONNABORTED');
        if (isCancelled) {
          return Promise.reject(error);
        }
        // Routed through logger so sanitizeForLogging scrubs sensitive
        // fields (password, token, Authorization headers) before console
        // emission. `responseData` can include server echoes of tokens on
        // some error paths, so it MUST go through the sanitiser.
        logger.debug('API Error Details', {
          url: error.config?.url,
          status: error.response?.status,
          method: error.config?.method?.toUpperCase(),
          message: error.message,
          code: error.code,
          isNetworkError: error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error'),
          responseData: error.response?.data,
        });
        if (error.response?.status === 401) {
          // Unauthorized - clear token and redirect to login
          // Handle SecureStore safely for web environment
          try {
            await SecureStore.deleteItemAsync('auth_token');
          } catch (secureStoreError) {
            // Fallback to localStorage for web
            if (typeof window !== 'undefined' && window.localStorage) {
              window.localStorage.removeItem('auth_token');
            }
          }

          // Only show session expired toast + force logout if this
          // wasn't a login request (a 401 on /auth/login is just
          // "wrong credentials", not session expiry).
          const isLoginRequest = error.config?.url?.includes('/auth/login');
          if (!isLoginRequest) {
            Toast.show({
              type: 'error',
              text1: 'Session Expired',
              text2: 'Please log in again',
            });
            // Notify the app so it can dispatch logoutUser() and let
            // AppNavigator swap back to the auth stack. Registered via
            // setOnSessionExpired() from App.tsx on mount.
            if (onSessionExpired) {
              try {
                onSessionExpired();
              } catch (cbError) {
                console.warn('onSessionExpired callback threw:', cbError);
              }
            }
          }
        } else if (error.response?.status >= 500) {
          Toast.show({
            type: 'error',
            text1: 'Server Error',
            text2: 'Something went wrong. Please try again.',
          });
        } else if (error.code === 'ECONNABORTED') {
          // Check if this is a file upload timeout
          const isUploadRequest = error.config?.url?.includes('/upload');
          if (isUploadRequest) {
            Toast.show({
              type: 'error',
              text1: 'Upload Timeout',
              text2: 'File analysis is taking longer than expected. Please try again or contact support.',
              visibilityTime: 6000,
            });
          } else {
            Toast.show({
              type: 'error',
              text1: 'Request Timeout',
              text2: 'The request took too long. Please try again.',
            });
          }
        }
        
        // FE-bug-audit (2026-05-27) — 4xx responses are EXPECTED business
        // logic, not errors:
        //   400 validation, 403 authz, 404 not-found, 409 conflict,
        //   410 gone, 422 schema, 429 rate-limit
        // The slice / consumer thunk handles them via rejectWithValue +
        // a user-visible toast or inline message. Logging them at error-
        // level was flooding Sentry with expected client-flow events
        // (e.g. "Only one reminder per 24 hours" 429s, terminal-state
        // 404s, recall-cooldown 429s, etc.).
        //
        // Downgrade to logger.warn so they're still visible in dev
        // console + breadcrumbs (Sentry breadcrumbs use warn-level OK)
        // but don't trigger the .error() pipeline that fires
        // Sentry.captureException as an error.
        const status = error.response?.status as number | undefined;
        const isExpected4xx = typeof status === 'number' && status >= 400 && status < 500;
        const logLevel = isExpected4xx ? 'warn' : 'error';
        logger[logLevel]('API Error', {
          status,
          data: error.response?.data,
          config: error.config,
        });

        return Promise.reject(error);
      }
    );
  }

  // Authentication endpoints
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      // Convert to form data for OAuth2 compliance
      const formData = new URLSearchParams();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);
      
      console.log('🔗 Making login request to:', `${API_BASE_URL}/auth/login`);
      console.log('📝 Form data:', formData.toString());
      
      const response = await this.api.post<AuthResponse>('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
        // Add these for CORS debugging
        withCredentials: false,
        timeout: 30000,
      });
      
      console.log('✅ Login response received:', {
        status: response.status,
        headers: response.headers,
        data: response.data
      });
      
      console.log('🔍 Login response data details:', {
        access_token: response.data.access_token ? 'Present' : 'Missing',
        token_type: response.data.token_type,
        user: response.data.user ? 'Present' : 'Missing',
        user_data: response.data.user
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ Login request failed:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        responseHeaders: error.response?.headers,
      });
      
      // Log the full error for debugging
      console.error('Full error object:', error);
      
      throw error;
    }
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/register', userData);
    return response.data;
  }

  /**
   * Exchange a Google ID token (GIS "credential") for a Remembr session.
   *
   * Backend: POST /auth/google → same LoginResponse shape as /auth/login.
   * Linking is implicit — if the Google email matches an existing
   * password account, the backend auto-links and returns the same row.
   */
  async loginWithGoogle(idToken: string): Promise<LoginResponse> {
    console.log('🔗 Making Google login request to:', `${API_BASE_URL}/auth/google`);
    const response = await this.api.post<LoginResponse>(
      '/auth/google',
      { id_token: idToken },
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        withCredentials: false,
        timeout: 30000,
      }
    );
    console.log('✅ Google login response:', {
      status: response.status,
      hasAccessToken: !!response.data.access_token,
      hasUser: !!response.data.user,
      requiresVerification: response.data.requires_verification,
    });
    return response.data;
  }

  async getProfile(): Promise<User> {
    const response = await this.api.get<User>('/auth/me');
    return response.data;
  }

  // Email verification endpoints
  async verifyEmail(code: string, verificationToken: string): Promise<VerificationResponse> {
    const response = await this.api.post<VerificationResponse>('/auth/verify-email', 
      { code }, 
      {
        headers: {
          'X-Internal-Token': verificationToken,
        },
      }
    );
    return response.data;
  }

  async resendVerificationCode(verificationToken: string): Promise<VerificationResponse> {
    const response = await this.api.post<VerificationResponse>('/auth/resend-verification-code', 
      {}, 
      {
        headers: {
          'X-Internal-Token': verificationToken,
        },
      }
    );
    return response.data;
  }

  async requestVerificationToken(credentials: LoginRequest): Promise<VerificationTokenResponse> {
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);
    
    const response = await this.api.post<VerificationTokenResponse>('/auth/request-verification-token', 
      formData, 
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    return response.data;
  }

  // Password reset endpoints
  async requestPasswordReset(email: string): Promise<PasswordResetRequestResponse> {
    const response = await this.api.post<PasswordResetRequestResponse>('/auth/request-password-reset', 
      { email }
    );
    return response.data;
  }

  async resetPassword(code: string, newPassword: string, resetToken: string): Promise<VerificationResponse> {
    const response = await this.api.post<VerificationResponse>('/auth/reset-password', 
      { 
        code, 
        new_password: newPassword 
      }, 
      {
        headers: {
          'X-Internal-Token': resetToken,
        },
      }
    );
    return response.data;
  }

  // Profile management endpoints
  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    const response = await this.api.put<User>('/auth/update-profile', data);
    return response.data;
  }

  async updatePassword(data: UpdatePasswordRequest): Promise<VerificationResponse> {
    const response = await this.api.put<VerificationResponse>('/auth/update-password', data);
    return response.data;
  }

  async updateEmail(data: UpdateEmailRequest): Promise<EmailUpdateResponse> {
    const response = await this.api.put<EmailUpdateResponse>('/auth/update-email', data);
    return response.data;
  }

  async getUserStats(): Promise<UserStats> {
    const response = await this.api.get<UserStats>('/auth/user-stats');
    return response.data;
  }

  async deleteAccount(): Promise<VerificationResponse> {
    const response = await this.api.delete<VerificationResponse>('/auth/delete-account');
    return response.data;
  }

  // Logout endpoint (optional - for token invalidation)
  async logout(): Promise<VerificationResponse> {
    try {
      console.log('🚪 API: logout() method called');
      logger.info('🚪 API: Calling backend logout endpoint');
      
      console.log('🚪 API: Making POST request to /auth/logout');
      const response = await this.api.post<VerificationResponse>('/auth/logout');
      
      console.log('✅ API: Backend logout response received:', response.status, response.data);
      logger.info('✅ API: Backend logout successful', { 
        message: response.data.message,
        success: response.data.success 
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ API: Backend logout failed with error:', error);
      console.error('❌ API: Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config?.url,
      });
      logger.error('❌ API: Backend logout failed', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      throw error;
    }
  }

  // Upload endpoints
  async uploadFile(
    file: { uri: string; name: string; type: string },
    description?: string,
    onUploadProgress?: (progress: number) => void
  ): Promise<UploadedFile> {
    const formData = new FormData();
    
    // Handle file upload differently for web vs mobile
    if (typeof window !== 'undefined' && window.File) {
      // Web environment - convert URI to File object
      try {
        const response = await fetch(file.uri);
        const blob = await response.blob();
        const fileObject = new File([blob], file.name, { type: file.type });
        formData.append('file', fileObject);
      } catch (error) {
        // Fallback: try to append as is
        formData.append('file', file as any);
      }
    } else {
      // Mobile environment
      formData.append('file', {
        uri: file.uri,
        name: file.name,
        type: file.type,
      } as any);
    }
    
    // Map MIME type to backend expected format ('image' or 'text')
    const getContentType = (mimeType: string): string => {
      if (mimeType.startsWith('image/')) {
        return 'image';
      } else if (mimeType.startsWith('text/') || 
                 mimeType.includes('document') || 
                 mimeType.includes('pdf') || 
                 mimeType.includes('word') || 
                 mimeType.includes('application/')) {
        return 'text';
      } else {
        // Default to text for unknown types
        return 'text';
      }
    };
    
    // Add content_type in the format the backend expects
    formData.append('content_type', getContentType(file.type));
    
    if (description) {
      formData.append('description', description);
    }

    const response = await this.api.post<UploadedFile>('/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 180000, // 3 minutes for file upload + analysis
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onUploadProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onUploadProgress(percentCompleted);
        }
      },
    });
    // The /upload/ response carries the row as `file_record_id` (there is no
    // `id` field). UploadedFile historically promised `.id`, so callers like
    // QuickAddSheet read `uploaded.id` — `undefined` at runtime, which dropped
    // `file_record_id` from the quick-add body and triggered a 422 (→ a crash
    // when the Pydantic error was rendered). Normalize so `.id` is always set.
    const data = response.data as UploadedFile & { file_record_id?: number };
    return { ...data, id: data.id ?? data.file_record_id };
  }

  async getFiles(skip = 0, limit = 20): Promise<PaginatedResponse<UploadedFile>> {
    // Convert to new pagination system
    const page = Math.floor(skip / limit) + 1;
    
    if (__DEV__) {
      console.log('🔄 getFiles conversion:', { 
        requestedSkip: skip, 
        requestedLimit: limit, 
        calculatedPage: page 
      });
    }
    
    const response = await this.getUserFiles(page, limit);
    
    // Transform new response to legacy format
    const files: UploadedFile[] = response.files.map(file => ({
      id: file.id,
      filename: file.filename || 'Untitled',
      content_type: file.file_type || file.content_type,
      file_type: file.file_type || file.content_type,
      file_size: file.file_size || 0,
      created_at: file.created_at,
      updated_at: file.updated_at,
      ai_description: file.ai_description,
      storage_url: file.storage_url || '',
      text_content: file.text_content,
      user_id: 0, // Will be filled by backend
      vector_collection: '', // Legacy field
    }));
    
    return {
      items: files,
      total: response.pagination.total,
      skip: response.pagination.offset,
      limit: response.pagination.limit,
      has_next: response.pagination.has_next,
    };
  }

  async deleteFile(fileId: string): Promise<DeleteFileResponse> {
    console.log('🗑️ API: deleteFile called with fileId:', fileId);
    console.log('🗑️ API: Making DELETE request to:', `/users/files/${fileId}`);

    const response = await this.api.delete<DeleteFileResponse>(`/users/files/${fileId}`);
    console.log('🗑️ API: Delete file response:', response.data);

    return response.data;
  }

  /** FE-210 / BE-205 — DELETE /users/files/{file_id}?cascade=...
   *
   *   - cascade=false (default): deletes the FileRecord; linked
   *     inventory rows have their `source_file_id` set to NULL but
   *     stay in the user's inventory.
   *   - cascade=true: deletes the FileRecord AND every userinventory
   *     row whose `source_file_id` matches. Aggressive — UI must
   *     confirm.
   *
   * Server returns 204 No Content; we resolve with `{ deleted: N }`
   * so callers can toast a count. Today the legacy `deleteFile` does
   * NOT accept the cascade param (it just removes the file row); the
   * new sceneDelete keeps the legacy unchanged and adds the cascade
   * variant so existing call sites don't have to migrate.
   */
  async deleteScene(
    fileId: number,
    options: { cascade: boolean },
  ): Promise<{ items_affected: number; cascade: boolean }> {
    const params = options.cascade ? '?cascade=true' : '?cascade=false';
    const response = await this.api.delete<{
      items_affected?: number;
      cascade?: boolean;
    } | null>(`/users/files/${fileId}${params}`);
    // Server may return 204 (no body) — normalize to {items_affected:0}.
    return {
      items_affected: response.data?.items_affected ?? 0,
      cascade: response.data?.cascade ?? options.cascade,
    };
  }

  // ──────────────────────────────────────────────────────────────────
  // FE-212 / BE-201 + BE-208 — Multi-photo per item.
  //
  // Cap: 4 user-added photos per item (5 total, with the PRIMARY_AUTO
  // row pinned at position 0). The upload endpoint accepts up to 4
  // files per call (server enforces both per-call and total caps).
  //
  // Reorder is user-added-only: position 0 is invariant and excluded
  // from the photo_ids list. Backend rejects any list that doesn't
  // exactly match the caller's set of user-added photos.
  // ──────────────────────────────────────────────────────────────────

  /** POST /inventory/{item_id}/photos — multipart upload, up to 4
   *  files per call. Each file becomes a USER_UPLOAD photo appended
   *  after the existing user-added rows. Returns the freshly-created
   *  PhotoSummary[] (NOT the full updated list — caller merges into
   *  state). Backend returns 413 Payload Too Large if the upload
   *  would push past the 4-user-photo cap. */
  async uploadItemPhotos(
    itemId: number,
    files: Array<{ uri: string; name: string; type: string }>,
  ): Promise<PhotoSummary[]> {
    const formData = new FormData();
    for (const f of files) {
      // Web environment — fetch the blob URI and append as a real File.
      // Native — append the RN file shape; axios picks it up via
      // the existing pattern in uploadFile() above.
      if (typeof window !== 'undefined' && window.File) {
        try {
          const blob = await (await fetch(f.uri)).blob();
          formData.append(
            'files',
            new File([blob], f.name, { type: f.type }),
          );
        } catch {
          formData.append('files', f as any);
        }
      } else {
        formData.append('files', {
          uri: f.uri,
          name: f.name,
          type: f.type,
        } as any);
      }
    }
    const response = await this.api.post<PhotoSummary[]>(
      `/inventory/${itemId}/photos`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 min — generous for slow networks + N files
      },
    );
    return response.data;
  }

  /** GET /inventory/{item_id}/photos — refresh the photo list.
   *  Most consumers read `InventoryItemResponse.photos[]` instead;
   *  this is the explicit fetch for post-mutation reconciliation
   *  (e.g., after a reorder we want canonical display_order values).
   *
   *  FE-249.3 — backend returns an envelope `{photos, active_count,
   *  cap}` from this endpoint AND from PATCH /reorder, but a flat
   *  array from POST /photos. (Verified live by BE 2026-05-22.)
   *  We unwrap defensively so callers always see a flat array, and
   *  accept both shapes in case the contract shifts later. */
  async listItemPhotos(itemId: number): Promise<PhotoSummary[]> {
    const response = await this.api.get<
      PhotoSummary[] | { photos: PhotoSummary[]; active_count?: number; cap?: number }
    >(`/inventory/${itemId}/photos`);
    const body = response.data as any;
    if (Array.isArray(body)) return body;
    return Array.isArray(body?.photos) ? body.photos : [];
  }

  /** DELETE /inventory/{item_id}/photos/{photo_id} — soft-delete a
   *  user-added photo. Backend rejects deletion of position-0
   *  (PRIMARY_AUTO) rows with 422. */
  async deleteItemPhoto(itemId: number, photoId: number): Promise<void> {
    await this.api.delete(`/inventory/${itemId}/photos/${photoId}`);
  }

  /** PATCH /inventory/{item_id}/photos/reorder — set the display
   *  order of user-added photos. `photo_ids` MUST be the FULL set of
   *  caller-owned user-added photos for this item, in the desired
   *  new order. Position 0 (PRIMARY_AUTO) is invariant and excluded.
   *
   *  FE-249.3 — same envelope unwrap as listItemPhotos. BE returns
   *  `{photos, active_count, cap}` here too. */
  async reorderItemPhotos(
    itemId: number,
    photoIds: number[],
  ): Promise<PhotoSummary[]> {
    const reqBody: PhotoReorderRequest = { photo_ids: photoIds };
    const response = await this.api.patch<
      PhotoSummary[] | { photos: PhotoSummary[]; active_count?: number; cap?: number }
    >(`/inventory/${itemId}/photos/reorder`, reqBody);
    const body = response.data as any;
    if (Array.isArray(body)) return body;
    return Array.isArray(body?.photos) ? body.photos : [];
  }

  async deleteInventoryItem(itemId: string): Promise<DeleteInventoryItemResponse> {
    console.log('🗑️ API: deleteInventoryItem called with itemId:', itemId);
    console.log('🗑️ API: Making DELETE request to:', `/users/inventory/${itemId}`);
    
    const response = await this.api.delete<DeleteInventoryItemResponse>(`/users/inventory/${itemId}`);
    console.log('🗑️ API: Delete inventory item response:', response.data);
    
    return response.data;
  }

  async getInventory(skip = 0, limit = 20): Promise<PaginatedResponse<InventoryItem>> {
    // Convert to new pagination system
    const page = Math.floor(skip / limit) + 1;
    const response = await this.getUserInventory(page, limit);
    
    // Transform new response to legacy format
    const items: InventoryItem[] = response.files
      .flatMap(file => file.inventory_items)
      .map(item => ({
        id: item.id || 0,
        item_name: item.item_name,
        item_description: item.item_description,
        item_category: item.item_category,
        location: item.location,
        tags: item.tags,
        confidence_score: item.confidence_score,
        created_at: '', // Will need to get from file
        updated_at: '', // Will need to get from file
        file_record_id: 0, // Will need to get from file
        user_id: 0, // Will be filled by backend
        brand: item.brand,
        condition: item.condition,
        estimated_value: item.estimated_value,
        is_verified: item.is_verified,
        notes: '', // Legacy field
      }));
    
    return {
      items,
      total: response.pagination.total,
      skip: response.pagination.offset,
      limit: response.pagination.limit,
    };
  }

  // Health check
  async getHealth(): Promise<HealthResponse> {
    const response = await this.api.get<HealthResponse>('/health');
    return response.data;
  }

  // Feedback endpoints
  async submitFeedback(feedbackData: FeedbackSubmissionRequest): Promise<FeedbackSubmissionResponse> {
    console.log('🔍 submitFeedback called with data:', feedbackData);
    console.log('🔍 Screenshot present:', !!feedbackData.screenshot);
    
    // Validate required fields before sending
    if (!feedbackData.feedback_type || !feedbackData.feedback_title || !feedbackData.feedback_content) {
      throw new Error('Missing required fields: feedback_type, feedback_title, or feedback_content');
    }
    
    // Validate field lengths
    if (feedbackData.feedback_title.length < 5 || feedbackData.feedback_title.length > 200) {
      throw new Error('Feedback title must be between 5 and 200 characters');
    }
    
    if (feedbackData.feedback_content.length < 10 || feedbackData.feedback_content.length > 5000) {
      throw new Error('Feedback content must be between 10 and 5000 characters');
    }
    
    // Validate rating if provided
    if (feedbackData.rating !== undefined && (feedbackData.rating < 0 || feedbackData.rating > 5)) {
      throw new Error('Rating must be between 0 and 5');
    }
    
    // Backend always expects multipart/form-data format
    console.log('📋 Using multipart/form-data (backend requirement)');
    const formData = new FormData();
    
    // Add all form fields with proper type conversion
    Object.entries(feedbackData).forEach(([key, value]) => {
      if (key !== 'screenshot' && value !== undefined && value !== null) {
        // Convert booleans and numbers to proper string representation
        let stringValue: string;
        if (typeof value === 'boolean') {
          stringValue = value.toString(); // 'true' or 'false'
        } else if (typeof value === 'number') {
          stringValue = value.toString();
        } else {
          stringValue = String(value);
        }
        console.log(`📋 Adding field: ${key} = ${stringValue} (type: ${typeof value})`);
        formData.append(key, stringValue);
      }
    });
    
    // Handle screenshot field - only add if screenshot exists
    if (feedbackData.screenshot) {
      console.log('📋 Adding screenshot file');
      formData.append('screenshot', feedbackData.screenshot);
    } else {
      console.log('📋 No screenshot - omitting screenshot field completely');
      // Don't add screenshot field at all when no screenshot (backend expects File(None))
    }
    
    console.log('📋 FormData prepared, sending request...');
    
    // Log complete FormData contents for debugging
    console.log('📋 Complete FormData contents:');
    const entries = Array.from(formData.entries());
    for (let [key, value] of entries) {
      console.log(`📋   ${key}: ${value} (${typeof value})`);
    }
    
    console.log('📋 Sending to PUBLIC endpoint - Authorization header removed');
    
    // Feedback endpoint is public - create a special config to skip auth header
    const response = await this.api.post<FeedbackSubmissionResponse>('/feedback/submit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000, // 30 seconds for feedback submission
      // Special flag to skip Authorization header in request interceptor
      skipAuth: true,
    } as any);
    
    console.log('✅ Feedback submitted successfully:', response.data);
    return response.data;
  }

  async getFeedbackTypes(): Promise<FeedbackTypesResponse> {
    const response = await this.api.get<FeedbackTypesResponse>('/feedback/types/list');
    return response.data;
  }

  // Enhanced User Statistics endpoint
  async getUserStatistics(refresh = false): Promise<UserStatisticsResponse> {
    const params = refresh ? '?refresh=true' : '';
    const response = await this.api.get<UserStatisticsResponse>(`/users/statistics${params}`);
    return response.data;
  }

  // Get individual file with fresh storage URL
  async getFileById(fileId: number | string): Promise<FileWithInventoryResponse> {
    console.log('🔗 Getting fresh URL for file ID:', fileId);
    const response = await this.api.get<FileWithInventoryResponse>(`/users/files/${fileId}`);
    console.log('✅ Fresh file URL retrieved:', response.data.storage_url);
    return response.data;
  }

  // Enhanced File Management endpoints
  async getUserFiles(page = 1, pageSize = 20, contentType?: 'image' | 'text'): Promise<FilesWithInventoryResponse> {
    // Convert page-based pagination to offset-based for the API
    const offset = (page - 1) * pageSize;
    const params = new URLSearchParams({
      limit: pageSize.toString(),
      offset: offset.toString(),
    });
    
    if (contentType) {
      params.append('content_type', contentType);
    }
    
    console.log('🔗 Making getUserFiles request to:', `/users/files?${params}`, {
      requestedPage: page,
      calculatedOffset: offset,
      limit: pageSize
    });
    const response = await this.api.get<FilesWithInventoryResponse>(`/users/files?${params}`);
    console.log('✅ getUserFiles response:', response.status, {
      filesCount: response.data.files?.length,
      pagination: response.data.pagination,
      requestedPage: page,
      firstFileId: response.data.files?.[0]?.id,
      lastFileId: response.data.files?.[response.data.files?.length - 1]?.id
    });
    
    return response.data;
  }

  async getUserInventory(
    page = 1,
    pageSize = 20,
    filters: {
      category?: string;
      is_verified?: boolean;
      min_value?: number;
      max_value?: number;
      search?: string;
      /** FE-221 / BE-210 — when true the response widens to include
       *  items where the caller is the current_custodian_user_id (in-crew
       *  handoffs they've received from owners). Default false preserves
       *  the legacy "owned-only" contract for callers that don't opt in. */
      held_by_me?: boolean;
    } = {}
  ): Promise<FilesWithInventoryResponse> {
    // Convert page-based pagination to offset-based
    const offset = (page - 1) * pageSize;
    const limit = pageSize;
    
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    
    // Add backend-specific filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        // Map frontend filter names to backend parameter names
        if (key === 'is_verified') {
          if (value === true) {
            params.append('verified_only', 'true');
          } else if (value === false) {
            params.append('verified_only', 'false');
          }
        } else if (key === 'held_by_me') {
          // FE-221 / BE-210 — only send the param when explicitly true
          // so the legacy "owned-only" default is preserved when the
          // FE just wants its own items (e.g., dashboard widgets).
          if (value === true) params.append('held_by_me', 'true');
        } else {
          params.append(key, value.toString());
        }
      }
    });
    
    console.log('🔗 Making getUserInventory request to:', `/users/inventory?${params}`);
    const response = await this.api.get<any>(`/users/inventory?${params}`);
    console.log('✅ getUserInventory response:', response.status, response.data);
    
    // Transform backend response to match our expected structure
    const backendPagination = response.data.pagination;
    const transformedResponse: FilesWithInventoryResponse = {
      files: [], // Not used for inventory endpoint, we use the inventory array directly
      pagination: {
        total: backendPagination.total,
        limit: backendPagination.limit,
        offset: backendPagination.offset,
        current_page: backendPagination.current_page,
        total_pages: backendPagination.total_pages,
        has_next: backendPagination.has_next,
        has_previous: backendPagination.has_previous,
      }
    };
    
    // Add the inventory items as a custom property for the slice to handle
    (transformedResponse as any).inventory = response.data.inventory;
    
    return transformedResponse;
  }

  // Enhanced inventory endpoint with full sharing information.
  // `opts.signal` lets callers abort an in-flight request — used by the
  // search-as-you-type flow in browseSlice to cancel stale fetches when
  // the query changes mid-roundtrip (server takes 0.5–2.5s).
  async getEnhancedInventory(
    params: EnhancedInventoryRequest = {},
    opts: { signal?: AbortSignal } = {},
  ): Promise<EnhancedInventoryResponse> {
    console.log('🔗 API: Getting enhanced inventory with params:', params);

    // Clean up parameters to avoid sending false/null/undefined values
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== false && value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const response = await this.api.get<EnhancedInventoryResponse>('/users/inventory', {
      params: cleanParams,
      signal: opts.signal,
    });
    console.log('✅ API: Enhanced inventory retrieved:', {
      itemCount: response.data.inventory?.length || 0,
      totalItems: response.data.pagination?.total || 0,
      page: response.data.pagination?.current_page || 1,
    });

    return response.data;
  }

  /**
   * GET /users/files-with-inventory — paginated. Backend uses
   * `limit` + `offset` query params (verified live via OpenAPI);
   * the legacy `page` + `page_size` shape was being silently
   * ignored, so every call effectively loaded just the first
   * 10 files. Fix translates the page-shaped public API to the
   * limit/offset wire shape so existing callers don't change.
   *
   * Used by Sprint 2.5's Scenes layout (FE-9.4.1) — paginates
   * over server-side scene-grouped data so a 2,760-item account
   * sees all their scans, not just the first 20 items' scenes.
   */
  async getFilesWithInventory(
    page = 1,
    pageSize = 20,
  ): Promise<FilesWithInventoryResponse> {
    const offset = Math.max(0, (page - 1) * pageSize);
    const params = new URLSearchParams({
      limit: String(pageSize),
      offset: String(offset),
    });

    const response = await this.api.get<FilesWithInventoryResponse>(
      `/users/files-with-inventory?${params}`,
    );
    return response.data;
  }

  // Signed URL Refresh endpoint
  async refreshFileUrls(fileIds: number[]): Promise<RefreshUrlsResponse> {
    const response = await this.api.post<RefreshUrlsResponse>('/users/refresh-file-urls', {
      file_ids: fileIds,
    });
    return response.data;
  }

  // Note: the legacy `uploadContent` POST /upload/ method was retired
  // when the web client switched to the streaming SSE scan endpoint.
  // See src/api/streamScan.ts for the active image-scan path.

  // Individual Detail Endpoints
  async getFileDetails(fileId: string, refreshUrl: boolean = false): Promise<FileDetailsResponse> {
    const response = await this.api.get<FileDetailsResponse>(
      `/users/files/${fileId}${refreshUrl ? '?refresh_url=true' : ''}`
    );
    return response.data;
  }

  async getInventoryItemDetails(itemId: number): Promise<InventoryItemDetailsResponse> {
    const response = await this.api.get<InventoryItemDetailsResponse>(`/users/inventory/${itemId}`);
    return response.data;
  }

  /** PATCH inventory item.
   *
   *  Optional `locationSource` lets callers tag the location-history
   *  event with the right origin (Phase 3F, backend#144). Backend
   *  defaults to MANUAL_EDIT when the header is absent. */
  async updateInventoryItem(
    itemId: number,
    updateData: UpdateInventoryItemRequest,
    options: {
      locationSource?: 'gps_button' | 'address_picker' | 'manual_edit';
    } = {},
  ): Promise<UpdateInventoryItemResponse> {
    const headers: Record<string, string> = {};
    if (options.locationSource) {
      headers['X-Location-Source'] = options.locationSource;
    }
    const response = await this.api.patch<UpdateInventoryItemResponse>(
      `/users/inventory/${itemId}`,
      updateData,
      Object.keys(headers).length > 0 ? { headers } : undefined,
    );
    return response.data;
  }

  /** Phase 3F — Location-change audit trail per backend#144.
   *  Returns every recorded mutation of an item's location fields,
   *  newest first. Server emits events for SCAN, MANUAL_EDIT,
   *  GPS_BUTTON, ADDRESS_PICKER, REVERSE_GEOCODE sources.
   *  Privacy: non-owner crew members get nulled lat/lng. */
  async getLocationHistory(
    itemId: number,
    params: { limit?: number; offset?: number } = {},
  ): Promise<LocationHistoryResponse> {
    const { limit = 20, offset = 0 } = params;
    const response = await this.api.get<LocationHistoryResponse>(
      `/inventory/${itemId}/location-history`,
      { params: { limit, offset } },
    );
    return response.data;
  }

  /** Phase 3H — Personalized label chips per backend#145.
   *  Returns the user's most-frequently-used location strings,
   *  case-normalized + de-duplicated, sorted by item count.
   *  Drives the LocationEditSheet quick-pick chip row. */
  async getTopLocations(
    limit: number = 8,
  ): Promise<TopLocationsResponse> {
    const response = await this.api.get<TopLocationsResponse>(
      '/users/locations/top',
      { params: { limit } },
    );
    return response.data;
  }

  // ===============================
  // NOTIFICATIONS (Sprint 8 — backend#151, BE-146)
  // ===============================
  // Five endpoints back the in-app inbox (live on prod via PR #154 +
  // #155). The bell badge polls `getUnreadNotificationCount` every
  // 60s (cheap, indexed COUNT query). NotificationsScreen fetches
  // the full list on mount.
  //
  // CTAs inside notification cards (Approve/Decline/Claim/Acknowledge)
  // call EXISTING domain endpoints (e.g. /groups/accept-invite) —
  // the notification row is just a surface, not the source of truth.
  // Server auto-marks rows read as a side-effect of domain actions,
  // so the badge clears regardless of which surface the user used.
  //
  // Wire-shape gotchas (BE-151 final spec — Cloud Build c41c5545
  // deployed):
  //   - unread-count returns `{ count }`, NOT `{ unread_count }`
  //   - read endpoint is `/{id}/read`, NOT `/{id}/mark-read`
  //   - read/dismiss return 204 (No Content) — no JSON body
  //   - mark-all-read returns `{ updated }`, accepts optional
  //     `{ before }` body for race-free "mark as of now" semantics
  //   - list response is `{ notifications, total, unread_count }` —
  //     no `limit/offset/has_more` (FE derives has_more locally)
  //
  // The client methods below normalize all of these into a stable
  // shape for the FE slice + selectors, so the rest of the app
  // never has to care about wire-format quirks.
  //
  // Graceful degrade: every method swallows a 404 (backend not yet
  // deployed) and returns a safe-empty result so the bell + screen
  // render cleanly during a rolling deploy.

  /** List the current user's notifications, newest first.
   *  Paginated via `offset`/`limit`. Server caps `limit` at 100;
   *  default 20. Pass `unread_only: true` for the unread-only
   *  feed (suppresses already-read rows). Dismissed rows are
   *  always excluded server-side. */
  async getNotifications(
    params: NotificationListParams = {},
  ): Promise<NotificationListResponse> {
    const { limit = 20, offset = 0, unread_only } = params;
    try {
      const response = await this.api.get<NotificationListResponse>(
        '/users/notifications',
        {
          params: {
            limit,
            offset,
            // Server expects `unread_only` (bool). Only forward
            // when caller explicitly opted in; default false.
            ...(unread_only !== undefined ? { unread_only } : {}),
          },
        },
      );
      return response.data;
    } catch (err: any) {
      // Backend-not-deployed fallback — bell stays empty, no
      // user-visible error. Sentry interceptor skips 404s.
      if (err?.response?.status === 404) {
        return {
          notifications: [],
          total: 0,
          unread_count: 0,
        };
      }
      throw err;
    }
  }

  /** Cheap polled endpoint — returns ONLY the unread count.
   *  NotificationBell uses this every 60s for the red badge dot
   *  without paying the full list fetch cost.
   *
   *  Wire shape: server returns `{ count }`. We normalize to the
   *  FE-internal `{ count }` shape so callers stay agnostic to
   *  the rename. */
  async getUnreadNotificationCount(): Promise<UnreadCountResponse> {
    try {
      const response = await this.api.get<UnreadCountResponse>(
        '/users/notifications/unread-count',
      );
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return { count: 0 };
      }
      throw err;
    }
  }

  /** Mark a single notification read. Server returns 204 No Content
   *  — we synthesize the result envelope client-side so the slice
   *  has a stable contract.
   *
   *  Idempotent server-side: re-reading an already-read row
   *  preserves the original `read_at`. The slice still calls this
   *  only when `read_at === null` so we don't burn requests. */
  async markNotificationRead(
    notificationId: number,
  ): Promise<MarkReadResult> {
    await this.api.post(
      `/users/notifications/${notificationId}/read`,
    );
    // Server returns 204 — synthesize the envelope. `read_at` is
    // a client-side ISO so timelines update immediately; the next
    // list fetch overwrites with the server's authoritative value.
    return {
      notification_id: notificationId,
      read_at: new Date().toISOString(),
    };
  }

  /** Mark every unread row read in a single call. Used by the
   *  NotificationsScreen "Mark all read" header action.
   *
   *  Sends an explicit `before` cutoff (= now) per backend's
   *  recommendation: prevents marking real-time events that
   *  landed during screen render. Server's tz-aware Z parser
   *  (PR #155) handles `new Date().toISOString()` directly. */
  async markAllNotificationsRead(
    before: string = new Date().toISOString(),
  ): Promise<MarkAllReadResponse> {
    const response = await this.api.post<MarkAllReadResponse>(
      '/users/notifications/mark-all-read',
      { before },
    );
    return response.data;
  }

  /** Soft-dismiss a notification. Server sets `dismissed_at = NOW()`
   *  and excludes the row from future list/count responses (audit
   *  trail preserved server-side). Returns 204 — we synthesize the
   *  envelope. Idempotent. */
  async dismissNotification(
    notificationId: number,
  ): Promise<{ notification_id: number; dismissed_at: string }> {
    await this.api.delete(`/users/notifications/${notificationId}`);
    return {
      notification_id: notificationId,
      dismissed_at: new Date().toISOString(),
    };
  }

  // ===============================
  // SEGMENTATION PIPELINE (feat/pipeline-v2)
  // ===============================
  // Write endpoints introduced with the segmentation rollout. See
  // docs/SEGMENTATION_PIPELINE_PRD.md (backend repo). Every write
  // captures an `annotation_feedback` row server-side for the
  // training data flywheel; clients just send the `interaction`
  // telemetry and forget about it.
  //
  // URL scheme note: these use `/inventory/{id}` (no `/users/`
  // prefix) per the new backend. Legacy `/users/inventory/{id}` is
  // untouched and still powers the Items-grid delete + Edit flows.

  /** Refine a mask. User drags a new bbox and/or taps
   *  foreground/background points, optionally skipping SAM3 via
   *  `use_box_as_mask: true`.
   *
   *  Auto-fallback: if SAM3 returns nothing with
   *  `use_box_as_mask: false`, the server silently saves the
   *  user-drawn bbox as a rect mask (`segmentation_model:
   *  'user_box_fallback'`) rather than 502'ing. */
  async resegmentInventoryItem(
    itemId: number,
    request: ResegmentRequest,
    opts?: { signal?: AbortSignal },
  ): Promise<ResegmentResponse> {
    const response = await this.api.post<ResegmentResponse>(
      `/inventory/${itemId}/resegment`,
      request,
      { signal: opts?.signal },
    );
    return response.data;
  }

  /** Add an item Vision missed. User draws a box, types a name,
   *  server creates the item with a rect mask
   *  (`segmentation_model: 'user_box'`). Returned item is shaped
   *  identically to the scan's `item` SSE payload.
   *
   *  **Deprecated for tap-add (#411).** New code should call
   *  `manualAnalyze` below instead — it folds this call + the
   *  follow-up `/inventory/{id}/re-analyze` into one round-trip
   *  that returns a fully AI-filled row. This method stays only
   *  as the kill-switch fallback if we need to revert. */
  async manualAddInventoryItem(
    request: ManualAddInventoryItemRequest,
    opts?: { signal?: AbortSignal },
  ): Promise<ManualAddInventoryItemResponse> {
    const response = await this.api.post<ManualAddInventoryItemResponse>(
      `/inventory/manual`,
      request,
      { signal: opts?.signal },
    );
    return response.data;
  }

  /** Tap-add AI flow (#411). The user taps (or drags) on the scan
   *  photo, the FE produces an image-pixel bbox, posts it here, and
   *  the BE returns a fully AI-filled UserInventory row in 3–7s.
   *
   *  Soft-failure is signalled by `error != null` on a 201 — the row
   *  is still created (bbox + mask are real) but the AI didn't fill
   *  the name/category/etc. The default placeholder name in that
   *  case is `(unrecognized — tap to name)`. Caller should open the
   *  rename input for that row.
   *
   *  Optional `pipeline` query param overrides the analyzer; omit
   *  unless the user opted into a non-default one (the default is
   *  `openai_grounded_retry`). */
  async manualAnalyze(
    request: ManualAnalyzeRequest,
    opts?: { signal?: AbortSignal; pipeline?: string },
  ): Promise<ManualAnalyzeResponse> {
    const url = opts?.pipeline
      ? `/inventory/manual-analyze?pipeline=${encodeURIComponent(opts.pipeline)}`
      : `/inventory/manual-analyze`;
    const response = await this.api.post<ManualAnalyzeResponse>(
      url,
      request,
      { signal: opts?.signal },
    );
    return response.data;
  }

  /** Permanently delete with an `annotation_feedback` snapshot
   *  (`event_type: 'delete'`) captured server-side BEFORE the row
   *  is soft-deleted and the vector embedding purged.
   *
   *  Distinct from the legacy `deleteInventoryItem(itemId: string)`
   *  above — that one hits `/users/inventory/{id}` and doesn't
   *  produce a feedback row. This is the new flow used from the
   *  segmentation review surface. */
  async deleteInventoryItemWithFeedback(
    itemId: number,
    request: DeleteInventoryItemV2Request = {},
    opts?: { signal?: AbortSignal },
  ): Promise<DeleteInventoryItemV2Response> {
    const response = await this.api.delete<DeleteInventoryItemV2Response>(
      `/inventory/${itemId}`,
      { data: request, signal: opts?.signal },
    );
    return response.data;
  }

  /** Commit the accepted + rejected sets in one round-trip.
   *  Accepted items are marked `is_verified = true` and a
   *  primary-photo job is spawned for each. Rejected items are
   *  soft-deleted and each gets an `annotation_feedback` row
   *  (`event_type: 'reject'`) snapshotting its SAM3-produced state.
   *  400 if any id appears in both lists. */
  async confirmInventoryBatch(
    request: ConfirmBatchRequest,
    opts?: { signal?: AbortSignal },
  ): Promise<ConfirmBatchResponse> {
    const response = await this.api.post<ConfirmBatchResponse>(
      `/inventory/confirm/batch`,
      request,
      {
        signal: opts?.signal,
        // Server kicks off async crop+upscale jobs but the synchronous
        // path also clears vector embeddings for rejected items + writes
        // annotation_feedback rows — bursty and slow under load. The
        // client default is 30s; bump to 90s here so we don't false-
        // alarm the user when AuraSR is queuing.
        timeout: 90000,
      },
    );
    return response.data;
  }

  // ===============================
  // ADMIN / FEEDBACK EXPORT
  // ===============================
  // Superuser-gated server-side. Clients should ALSO hide these UIs
  // behind a role check, but the HEAD probe is the authoritative
  // lever (see segmentation PRD §8 security).

  async getAdminAnnotationFeedback(
    params?: AdminAnnotationFeedbackParams,
  ): Promise<AnnotationFeedbackRow[]> {
    const response = await this.api.get<AnnotationFeedbackRow[]>(
      `/admin/annotation-feedback`,
      { params },
    );
    return response.data;
  }

  /** Stream the feedback table as newline-delimited JSON. Returns
   *  a Blob the caller can `URL.createObjectURL` + download. The
   *  backend's `Content-Type: application/x-ndjson` is preserved. */
  async dumpAnnotationFeedbackJsonl(): Promise<Blob> {
    const response = await this.api.get(`/admin/annotation-feedback.jsonl`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  // ===============================
  // SHARING ENDPOINTS
  // ===============================

  // Create sharing link for an item
  async createShare(itemId: number, request: CreateShareRequest): Promise<ShareResponse> {
    const response = await this.api.post<ShareResponse>(`/users/inventory/${itemId}/share`, request);
    return response.data;
  }

  // Get user's sharing activities
  async getUserShares(params?: {
    status?: string;
    sharing_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<ShareListResponse> {
    const response = await this.api.get<ShareListResponse>('/users/shares', { params });
    return response.data;
  }

  // Manage share status (return, complete, cancel, decline)
  async manageShare(shareId: number, request: ShareManageRequest): Promise<ShareManageResponse> {
    const response = await this.api.patch<ShareManageResponse>(`/users/shares/${shareId}`, request);
    return response.data;
  }

  // Send reminder email to borrower for a share (owner only)
  async sendShareReminder(shareId: number): Promise<{ success: boolean; message: string }>
  {
    console.log('📧 API: Sending reminder for share:', shareId);
    const response = await this.api.post<{ success: boolean; message: string }>(`/users/shares/${shareId}/send-reminder`, {});
    console.log('✅ API: Reminder sent:', response.data);
    return response.data;
  }

  // Update item visibility (public/private)
  async updateItemVisibility(itemId: number, request: VisibilityUpdateRequest): Promise<VisibilityUpdateResponse> {
    const response = await this.api.patch<VisibilityUpdateResponse>(`/users/inventory/${itemId}/visibility`, request);
    return response.data;
  }



  // ===============================
  // PUBLIC SHARING ENDPOINTS
  // ===============================

  // View public share (no auth required)
  async viewPublicShare(shareToken: string): Promise<ShareViewResponse> {
    const response = await axios.get<ShareViewResponse>(`${API_BASE_URL}/share/${shareToken}`);
    return response.data;
  }

  // Claim shared item (auth required)
  async claimSharedItem(shareToken: string, request: ClaimRequest): Promise<ClaimResponse> {
    const response = await this.api.post<ClaimResponse>(`/share/${shareToken}/claim`, request);
    return response.data;
  }

  // ===============================
  // PUBLIC DISCOVERY ENDPOINTS
  // ===============================
  
  // Browse public items from all users
  async getPublicItems(params?: PublicItemsRequest): Promise<PublicItemsResponse> {
    console.log('🔍 API: Getting public items with params:', params);
    const response = await this.api.get<PublicItemsResponse>('/public/items', { params });
    console.log('✅ API: Public items retrieved:', {
      itemCount: response.data.items?.length || 0,
      totalItems: response.data.total_items,
      page: response.data.page
    });
    return response.data;
  }

  // ===============================
  // BORROW REQUEST ENDPOINTS
  // ===============================
  
  // Send a borrow request for a public item
  async createBorrowRequest(itemId: number, request: CreateBorrowRequestSchema): Promise<BorrowRequestResponse> {
    console.log('📤 API: Creating borrow request for item:', itemId, 'with data:', request);
    const response = await this.api.post<BorrowRequestResponse>(`/public/items/${itemId}/request-borrow`, request);
    console.log('✅ API: Borrow request created:', {
      requestId: response.data.request_id,
      ownerNotified: response.data.owner_notified,
      expiresAt: response.data.expires_at
    });
    return response.data;
  }

  // Get borrow requests received by current user (as owner)
  async getBorrowRequests(params?: BorrowRequestsQuery): Promise<BorrowRequestListResponse> {
    console.log('📥 API: Getting borrow requests with params:', params);
    const response = await this.api.get<BorrowRequestListResponse>('/users/borrow-requests', { params });
    console.log('✅ API: Borrow requests retrieved:', {
      totalPending: response.data.total_pending,
      totalAll: response.data.total_all,
      requestCount: response.data.requests?.length || 0
    });
    return response.data;
  }

  // Get borrow requests SENT by current user (as requester)
  async getSentBorrowRequests(): Promise<SentBorrowRequestListResponse> {
    console.log('📤 API: Getting sent borrow requests');
    const response = await this.api.get<SentBorrowRequestListResponse>('/users/sent-borrow-requests');
    console.log('✅ API: Sent requests retrieved:', {
      totalPending: response.data.total_pending,
      totalAll: response.data.total_all,
      requestCount: response.data.requests?.length || 0,
    });
    return response.data;
  }

  // Claim an approved borrow request (requester-only)
  async claimSentBorrowRequest(requestId: number, body: { message?: string } = {}): Promise<ClaimResponse> {
    console.log('📦 API: Claiming approved borrow request:', requestId, body?.message ? 'with message' : '');
    const response = await this.api.post<ClaimResponse>(`/users/sent-borrow-requests/${requestId}/claim`, body);
    console.log('✅ API: Borrow request claimed:', response.data);
    return response.data;
  }

  // Cancel a sent borrow request (requester)
  async cancelSentBorrowRequest(requestId: number): Promise<SentBorrowCancelResponse> {
    console.log('🛑 API: Cancelling sent borrow request:', requestId);
    const response = await this.api.patch<SentBorrowCancelResponse>(`/users/sent-borrow-requests/${requestId}/cancel`);
    console.log('✅ API: Sent request cancelled:', response.data.request_id);
    return response.data;
  }

  // Approve or deny a borrow request (owner only)
  async manageBorrowRequest(requestId: number, action: BorrowRequestActionSchema): Promise<BorrowRequestActionResponse> {
    console.log('⚖️ API: Managing borrow request:', requestId, 'with action:', action.action);
    const response = await this.api.patch<BorrowRequestActionResponse>(`/users/borrow-requests/${requestId}`, action);
    console.log('✅ API: Borrow request managed:', {
      action: response.data.action,
      shareCreated: response.data.share_created,
      shareUrl: response.data.share_url ? 'Created' : 'None'
    });
    return response.data;
  }

  // Get sharing contacts - frequent sharing partners
  async getSharingContacts(params?: SharingContactsRequest): Promise<SharingContactsResponse> {
    console.log('👥 API: Getting sharing contacts with params:', params);
    const response = await this.api.get<SharingContactsResponse>('/users/sharing-contacts', { params });
    console.log('✅ API: Sharing contacts retrieved:', {
      contactCount: response.data.contacts?.length || 0,
      contacts: response.data.contacts?.map(c => ({ email: c.email, name: c.name, timesShared: c.times_shared }))
    });
    return response.data;
  }

  // ====================================================================
  // Workspaces / Groups (Chunk 8 — Sprint 1)
  // ====================================================================

  async listGroups(params?: { include_archived?: boolean }): Promise<ListGroupsResponse> {
    // Sprint 1 backend returns a bare array. backend#103 may add a
    // `{groups: [...]}` envelope later — accept either shape so we
    // don't break on the day they ship the fix.
    const response = await this.api.get<ListGroupsResponse | { groups: ListGroupsResponse }>(
      '/groups',
      { params },
    );
    const data = response.data as any;
    return Array.isArray(data) ? data : (data?.groups ?? []);
  }

  async getGroup(groupId: number): Promise<GroupDetail> {
    const response = await this.api.get<GroupDetail>(`/groups/${groupId}`);
    return response.data;
  }

  async createGroup(request: CreateGroupRequest): Promise<GroupDetail> {
    const response = await this.api.post<GroupDetail>('/groups', request);
    return response.data;
  }

  async patchGroup(groupId: number, request: PatchGroupRequest): Promise<GroupDetail> {
    const response = await this.api.patch<GroupDetail>(`/groups/${groupId}`, request);
    return response.data;
  }

  async inviteGroupMember(
    groupId: number,
    request: InviteMemberRequest,
  ): Promise<InviteMemberResponse> {
    const response = await this.api.post<InviteMemberResponse>(
      `/groups/${groupId}/members`,
      request,
    );
    return response.data;
  }

  async removeGroupMember(groupId: number, userId: number): Promise<void> {
    await this.api.delete(`/groups/${groupId}/members/${userId}`);
  }

  /** DELETE /groups/{group_id}/members/by-email/{email} (BE-212 / #209).
   *
   *  Cancels a still-INVITED row by email (no `user_id` exists for the
   *  invitee yet). Auth: OWNER or ADMIN. Side effect: if the invitee
   *  already has a Founsi account, their CREW_INVITE_RECEIVED
   *  notification is marked read in the same transaction so they don't
   *  see a stale invite tile after the inviter pulled the plug.
   *
   *  Returns 204; throws 404 when there's no INVITED row for that
   *  email; 403 for non-admin callers.
   *
   *  Per Scenarios.md SG-17: a cancel-by-email counts as a "decline"
   *  for the rolling-window cooldown so the inviter can't spam-recycle
   *  by repeatedly inviting + canceling + re-inviting. */
  async cancelGroupInviteByEmail(
    groupId: number,
    email: string,
  ): Promise<void> {
    // Email goes in the path — URL-encode to handle '+' aliases etc.
    await this.api.delete(
      `/groups/${groupId}/members/by-email/${encodeURIComponent(email)}`,
    );
  }

  async patchGroupMemberRole(
    groupId: number,
    userId: number,
    request: PatchMemberRoleRequest,
  ): Promise<GroupMember> {
    const response = await this.api.patch<GroupMember>(
      `/groups/${groupId}/members/${userId}`,
      request,
    );
    return response.data;
  }

  async acceptGroupInvite(
    request: AcceptInviteRequest,
  ): Promise<AcceptInviteResponse> {
    const response = await this.api.post<AcceptInviteResponse>(
      '/groups/accept-invite',
      request,
    );
    return response.data;
  }

  /** GET /groups/invites/received — list pending invites for the
   *  signed-in user (matched by their email address). Powers the
   *  CrewHub pending-invites banner. backend#114, live on prod. */
  async getReceivedInvites(): Promise<ReceivedInvitesResponse> {
    const response = await this.api.get<ReceivedInvitesResponse>(
      '/groups/invites/received',
    );
    return response.data;
  }

  /* ── Sprint 2 (Chunk 9) — item↔group binding + auto-suggest ────── */

  /** POST /groups/{id}/items. Bulk-bind items the caller owns to
   *  a group. Items the caller doesn't own are silently dropped
   *  (returned in `skipped`) — see BE-9.2's authz pattern. */
  async addItemsToGroup(
    groupId: number,
    request: AddItemsToGroupRequest,
  ): Promise<AddItemsToGroupResponse> {
    const response = await this.api.post<AddItemsToGroupResponse>(
      `/groups/${groupId}/items`,
      request,
    );
    return response.data;
  }

  /** DELETE /groups/{id}/items/{item_id}. Unbinds the item from
   *  the group; the item itself stays in the user's inventory. */
  async removeItemFromGroup(groupId: number, itemId: number): Promise<void> {
    await this.api.delete(`/groups/${groupId}/items/${itemId}`);
  }

  /** GET /groups/{id}/inventory. Paginated. Server returns
   *  `GroupItemSummary` rows including the Sprint 2 fields
   *  (`scan_lat`, `scan_lng`) that /users/inventory doesn't yet
   *  expose. (FE-214 — `serial_number` dropped on BE-202.) */
  async getGroupInventory(
    groupId: number,
    params?: { limit?: number; offset?: number },
  ): Promise<GroupInventoryResponse> {
    const response = await this.api.get<GroupInventoryResponse>(
      `/groups/${groupId}/inventory`,
      { params },
    );
    return response.data;
  }

  /** GET /groups/{id}/rules. Returns all rules belonging to this
   *  group; ordered by `priority` server-side. */
  async listGroupRules(groupId: number): Promise<GroupRule[]> {
    const response = await this.api.get<GroupRule[]>(
      `/groups/${groupId}/rules`,
    );
    return response.data;
  }

  /** POST /groups/{id}/rules. */
  async createGroupRule(
    groupId: number,
    request: CreateGroupRuleRequest,
  ): Promise<GroupRule> {
    const response = await this.api.post<GroupRule>(
      `/groups/${groupId}/rules`,
      request,
    );
    return response.data;
  }

  /** DELETE /groups/{id}/rules/{rule_id}. ⚠️ No PATCH endpoint
   *  exists — to "edit" a rule, delete + recreate. */
  async deleteGroupRule(groupId: number, ruleId: number): Promise<void> {
    await this.api.delete(`/groups/${groupId}/rules/${ruleId}`);
  }

  /* ── Sprint 3 / Chunk 10A — Custody flow ─────────────────────── */

  /** POST /inventory/{id}/handoff — owner-push. Custody flips
   *  immediately + an `item_custody_event` row is written. The
   *  recipient gets a `handoff_sent` email (BE-10.11a). Returns
   *  the flip confirmation + the event row in one shot. */
  async handoffItem(
    itemId: number,
    request: HandoffRequest,
  ): Promise<CustodyFlipResponse> {
    const response = await this.api.post<CustodyFlipResponse>(
      `/inventory/${itemId}/handoff`,
      request,
    );
    return response.data;
  }

  /** POST /inventory/{id}/handoff-request — member-pull. If the
   *  group's `auto_approve_handoff = true`, the server flips
   *  custody immediately and `auto_approved` returns true. Otherwise
   *  a pending `borrow_requests` row is created and the owner gets
   *  a notification — the response includes a `request_id`. */
  async requestHandoff(
    itemId: number,
    request: HandoffRequestRequest,
  ): Promise<HandoffRequestResponse> {
    const response = await this.api.post<HandoffRequestResponse>(
      `/inventory/${itemId}/handoff-request`,
      request,
    );
    return response.data;
  }

  /** POST /inventory/{id}/return — current custodian flips custody
   *  back to the owner. Optional condition photo + note. */
  async returnItem(
    itemId: number,
    request: ReturnRequest = {},
  ): Promise<CustodyFlipResponse> {
    const response = await this.api.post<CustodyFlipResponse>(
      `/inventory/${itemId}/return`,
      request,
    );
    return response.data;
  }

  /** POST /inventory/{id}/remind (FE-222 / SG-16 — backend rev 00238+).
   *
   *  Owner-only nudge to the current custodian. Fires both an email
   *  (SendPulse) and an in-app `CUSTODY_REMINDER` notification. Server
   *  rate-limits to once per 24h per (owner, item) — second call returns
   *  429 with `next_reminder_allowed_at` so the client can show the
   *  cooldown timer instead of allowing the tap.
   *
   *  Returns `{ ok, item_id, custodian_user_id, reminded_at, days_held,
   *             next_reminder_allowed_at }` on success.
   *
   *  Authz: item.user_id == caller AND item.current_custodian_user_id
   *  != null AND custody is out. Otherwise 403/404 leak. */
  async remindCustodian(
    itemId: number,
    request: { custom_message?: string } = {},
  ): Promise<{
    ok: boolean;
    item_id: number;
    custodian_user_id: number;
    reminded_at: string;
    days_held: number;
    next_reminder_allowed_at: string;
  }> {
    const response = await this.api.post<any>(
      `/inventory/${itemId}/remind`,
      request,
    );
    return response.data;
  }

  /** POST /inventory/{id}/recall-acknowledge (Sprint 8 / BE-147).
   *  Current custodian acks an outstanding recall — flips custody
   *  back to owner in a single transaction, marks the RECALL
   *  borrow_requests row FULFILLED, writes a RECALL_ACK custody
   *  event, and emits a CUSTODY_RECALL_ACK notification to the owner.
   *
   *  Authz: current_custodian only. 404 leak for anyone else
   *  (matches handoff/return convention). Returns the same shape
   *  as `/return` — caller can refresh ItemDetail off the response. */
  async acknowledgeRecall(
    itemId: number,
  ): Promise<CustodyFlipResponse> {
    const response = await this.api.post<CustodyFlipResponse>(
      `/inventory/${itemId}/recall-acknowledge`,
    );
    return response.data;
  }

  /** GET /inventory/{id}/custody — full timeline of custody events
   *  for the item, plus the current custodian + due date snapshot
   *  for convenience. Sprint 3 frontend renders this on
   *  ItemDetailScreen as the history section. */
  async getCustodyHistory(itemId: number): Promise<CustodyHistoryResponse> {
    const response = await this.api.get<CustodyHistoryResponse>(
      `/inventory/${itemId}/custody`,
    );
    return response.data;
  }

  /** POST /inventory/{id}/recall — owner forces a return on an item
   *  currently held by someone else. Server creates a high-priority
   *  borrow_requests row + sends a "please return ASAP" email to the
   *  holder. Sprint 4 / Chunk 10B FE-10.3. */
  async recallItem(itemId: number, body: RecallRequest = {}): Promise<RecallResponse> {
    const response = await this.api.post<RecallResponse>(
      `/inventory/${itemId}/recall`,
      body,
    );
    return response.data;
  }

  /** GET /search/resolve — cross-group resolver. Searches every
   *  group the caller is a member of plus their personal inventory.
   *  Returns ResolvedItem rows with owner + custodian names + crew
   *  membership pre-joined. Sprint 4 / Chunk 10B FE-10.7. */
  async resolveSearch(
    q: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<ResolveResponse> {
    const response = await this.api.get<ResolveResponse>('/search/resolve', {
      params: { q, ...params },
    });
    return response.data;
  }

  /** GET /groups/{id}/activity — paginated, group-scoped activity
   *  feed. Each event is denormalized (actor / from / to user names
   *  + item name pre-joined) so the frontend can render rows without
   *  a separate /users lookup. Sprint 4 / Chunk 10B FE-10.6. */
  async getGroupActivity(
    groupId: number,
    params: { limit?: number; offset?: number } = {},
  ): Promise<GroupActivityResponse> {
    const response = await this.api.get<GroupActivityResponse>(
      `/groups/${groupId}/activity`,
      { params },
    );
    return response.data;
  }

  /* ──────────────────────────────────────────────────────────────────────
   * Sprint 5 / Chunk 11 — Packs
   *
   * Backend endpoints live on prod (BE-11.1 through 11.5, plus the
   * Chunk 12 wrap bonus). Field names match the OpenAPI spec.
   * ────────────────────────────────────────────────────────────────────── */

  /** POST /groups/{group_id}/packs — create a pack. (BE-11.2)
   *  Items are NOT included on create — call `addPackItems` after to
   *  bind items to the new pack. */
  async createPack(
    groupId: number,
    body: CreatePackRequest,
  ): Promise<PackSummary> {
    const response = await this.api.post<PackSummary>(
      `/groups/${groupId}/packs`,
      body,
    );
    return response.data;
  }

  /** GET /groups/{group_id}/packs — list packs in a crew. (BE-11.3) */
  async getGroupPacks(groupId: number): Promise<PackListResponse> {
    const response = await this.api.get<PackListResponse>(
      `/groups/${groupId}/packs`,
    );
    return response.data;
  }

  /** GET /packs/{pack_id} — full pack detail with items. (BE-11.3) */
  async getPack(packId: number): Promise<PackDetail> {
    const response = await this.api.get<PackDetail>(`/packs/${packId}`);
    return response.data;
  }

  /** PATCH /packs/{pack_id} — rename / dates / status. (BE-11.4) */
  async patchPack(
    packId: number,
    body: PatchPackRequest,
  ): Promise<PackSummary> {
    const response = await this.api.patch<PackSummary>(
      `/packs/${packId}`,
      body,
    );
    return response.data;
  }

  /** DELETE /packs/{pack_id} — delete a pack entirely. */
  async deletePack(packId: number): Promise<void> {
    await this.api.delete(`/packs/${packId}`);
  }

  /** POST /packs/{pack_id}/items — bulk add items. (BE-11.5)
   *  Server returns three categories:
   *    - added[]: PackItem rows
   *    - skipped_already_bound[]: ids already on the pack
   *    - skipped_not_in_group[]: ids not bound to the pack's crew */
  async addPackItems(
    packId: number,
    body: AddPackItemsRequest,
  ): Promise<AddPackItemsResponse> {
    const response = await this.api.post<AddPackItemsResponse>(
      `/packs/${packId}/items`,
      body,
    );
    return response.data;
  }

  /** DELETE /packs/{pack_id}/items/{item_id} — remove single item. (BE-11.5) */
  async removePackItem(packId: number, itemId: number): Promise<void> {
    await this.api.delete(`/packs/${packId}/items/${itemId}`);
  }

  /** POST /packs/{pack_id}/wrap — finalize the pack. Chunk 12 bonus.
   *  Server auto-returns items still with the owner, reports counts. */
  async wrapPack(packId: number): Promise<WrapPackResponse> {
    const response = await this.api.post<WrapPackResponse>(
      `/packs/${packId}/wrap`,
      {},
    );
    return response.data;
  }

  /* ──────────────────────────────────────────────────────────────────────
   * Sprint 5 / Chunk 11.5 — Quick-add + voice metadata
   * Backend endpoints live on prod (BE-11.5.1 + BE-11.5.2).
   * ────────────────────────────────────────────────────────────────────── */

  /** POST /inventory/quick-add — single-item creation. Caller uploads
   *  a photo via `uploadFile` first to get a `file_record_id`, then
   *  fires this with the metadata fields. (BE-11.5.1) */
  async quickAddItem(body: QuickAddRequest): Promise<QuickAddResponse> {
    const response = await this.api.post<QuickAddResponse>(
      '/inventory/quick-add',
      body,
    );
    return response.data;
  }

  /** POST /inventory/{id}/extract-metadata — server-side LLM parses
   *  a voice transcript into structured fields (purchase_date,
   *  estimated_value, warranty_years, brand, notes). Caller reviews
   *  + confirms the extracted fields before save. (BE-11.5.2 / Flow Q)
   *  (FE-214 — `serial_number` removed from the extracted shape after
   *  BE-202 dropped the column.) */
  async extractMetadataFromTranscript(
    itemId: number,
    body: ExtractMetadataRequest,
  ): Promise<ExtractMetadataResponse> {
    const response = await this.api.post<ExtractMetadataResponse>(
      `/inventory/${itemId}/extract-metadata`,
      body,
    );
    return response.data;
  }

  /* ════════════════════════════════════════════════════════════
   * Scan History (FE #124 / backend#312 + #313)
   *
   * The BE exposed `GET /users/scans` + `GET /users/scans/{id}` +
   * `POST /upload/{id}/segment/stream` from day one but no FE surface
   * called them. This block wires them. See USJ.md J2.B4 v2 for the
   * UX they power.
   * ════════════════════════════════════════════════════════════ */

  /** `GET /users/scans` — list the user's scans, lightweight payload
   *  per backend#312. Sort defaults match the production app's pattern
   *  (created_at desc — newest first). The optional `status` filter
   *  powers the cheap Dashboard banner check
   *  (`?status=IN_PROGRESS&limit=1`). */
  async getUserScans(
    params: ScanHistoryListParams = {},
  ): Promise<ScanHistoryResponse> {
    const q = new URLSearchParams();
    q.append('limit', String(params.limit ?? 20));
    q.append('offset', String(params.offset ?? 0));
    q.append('sort_by', params.sort_by ?? 'created_at');
    q.append('sort_order', params.sort_order ?? 'desc');
    if (params.status) q.append('status', params.status);
    const response = await this.api.get<ScanHistoryResponse>(
      `/users/scans?${q.toString()}`,
    );
    return response.data;
  }

  /** `GET /users/scans/{file_record_id}` — full scan detail with
   *  nested `inventory_items`. Used when the FE needs to resume a
   *  scan into SegmentScreen (e.g., from the Scan History page's
   *  Resume CTA). Heavier payload than the list endpoint by design. */
  async getUserScan(fileRecordId: number): Promise<ScanDetailResponse> {
    const response = await this.api.get<ScanDetailResponse>(
      `/users/scans/${fileRecordId}`,
    );
    return response.data;
  }

  /** `DELETE /users/scans/{file_record_id}` — discard an unfinished
   *  scan (status: IN_PROGRESS / READY_TO_SAVE / FAILED). Cascade
   *  removes any pre-confirm `inventory_items` rows; items that
   *  already passed through `/inventory/confirm/batch` (saved_at !=
   *  null) are NEVER touched. Filed as FE #127 backlog → BE work
   *  paired with backend#312 / #313 in the current sprint. */
  async deleteScan(fileRecordId: number): Promise<void> {
    await this.api.delete(`/users/scans/${fileRecordId}`);
  }

  /** Resolves the full SSE URL for `POST /upload/{file_record_id}/segment/stream`.
   *  This endpoint already exists; per backend#313 it's idempotent on
   *  a FAILED scan (replays the AI pipeline without re-upload, cleans
   *  up pre-confirm items first). FE callers should use this URL with
   *  the existing SegmentScreen SSE consumer (already wired for
   *  `/upload/scan/stream`); the event types are identical. */
  buildSegmentStreamUrl(fileRecordId: number): string {
    return `${this.api.defaults.baseURL}/upload/${fileRecordId}/segment/stream`;
  }
}

export const api = new RemembrApi();
export default api; 